import { FIELD_VARIABLE_REGEX } from '@/components/common/rich_input/RichInputHelper';
import { DateMode, type FormField, FormFieldChoice, FormFieldType, MatrixColumnType, type MatrixTableRow, OTHER_CHOICE_CODE } from '@/models';
import {
  convertStringHoursToNumberHours,
  getOnlyTimeSeconds,
  getTimeFromSeconds,
  getUTCJSDate,
  getUTCSeconds,
  getUTCSecondsAtStartOfDay,
  roundNumber 
} from '@/utils';
import { DateTime } from 'luxon';
import { checkIfMatrixHasValidValues } from './items/MatrixHelper';

// Define the types for operators
type LogicalOperator = '&&' | '||' | '!&&' | '!||' | '!' | '?::';
type ComparisonOperator = '==' | '!=' | '<' | '<=' | '>' | '>=' | '⊂' | '⊂=' | '⊃' | '⊃=';
type MathOperator = '+' | '-' | '*' | '/';
type OtherOperator = '?' | '??';

// Union of all possible operators
type ConditionOperator = LogicalOperator | ComparisonOperator | MathOperator | OtherOperator;

// Define the effect types (you can add more as needed)
type EffectType = 'Show' | 'Hide' | 'Warn' | 'Error' | 'Disable' | 'Enable' | 'Assign' | 'Valid';

// Define a recursive condition type that can represent nested conditions
// @ts-expect-error circular definition
type Condition =
  | [ConditionOperator, ...Array<Condition>]   // Operator with nested conditions
  | string                                    // An identifier or a literal value
  | number                                    // A number value
  | boolean;                                  // A boolean value


// Define the structure of an effect
export type Effect = [EffectType, ...Array<Condition>];


export const computeEffectProps = (field: FormField, formFields: FormField[]) => {
  if (field.type === FormFieldType.BRANDING || field.type === FormFieldType.SUBMIT_BUTTON) {
    return;
  }
  const effects = sortByActions(field.conditions as Effect[], ACTIONS_ORDER);

  (effects as Effect[])?.forEach(([type, ...args]: Condition[]) => {
    let shouldApplyEffect: boolean = false;
    let message: string | undefined = '';
    let condition: Condition;

    switch (type) {
      case 'Show':
        if (args.length === 2) {
          [, condition] = args;
          shouldApplyEffect = evaluateCondition(condition, formFields);
        } else if (args.length === 1 && Array.isArray(args[0])) {
          [condition] = args;
          shouldApplyEffect = evaluateCondition(condition, formFields);
        } else if (args.length === 1) {
          // [identifier] = args; NB. Currently the identifier is being ignored
        } else if (args.length === 0) {
          shouldApplyEffect = true;
        }
        field.show = shouldApplyEffect;
        if (!field.show) {
          clearFieldValue(field);
        }
        break;

      case 'Warn':
      case 'Error':
        if (field.inputChanged) {
          if (args.length === 2) {
            [message, condition] = args;
          } else if (args.length === 3) {
            [message, , condition] = args;
          }
          shouldApplyEffect = evaluateCondition(condition, formFields);
          if (shouldApplyEffect) {
            if (type === 'Warn') {
              field.warn = message;
            } else {
              field.error = message;
            }
          } else {
            delete field.warn;
            delete field.error;
          }
        }
        break;

      case 'Valid':
        //need to rework on this
        if (args.length === 2) {
          [message, condition] = args;
        } else if (args.length === 3) {
          [message, , condition] = args;
        }
        shouldApplyEffect = evaluateCondition(condition, formFields);
        if (!shouldApplyEffect) {
          field.valid = message;
        } else {
          delete field.valid;
        }
        break;

      case 'Disable':
        // TODO: add conditions logic in the form builder first
        field.disable = true;
        break;

      case 'Hide':
        field.show = false;
        break;

      case 'Strict-Required':
        field.strictRequired = true;
        // eslint-disable-next-line no-case-declarations
        const fieldValue = getFieldValue(field);
        if (
          field.type === FormFieldType.MATRIX &&
          fieldValue
        ) {
          const matrixRows = fieldValue as MatrixTableRow[];
          field.showRequiredError = !checkIfMatrixHasValidValues(field, matrixRows);
        } else if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
          field.showRequiredError = false;
        } else {
          field.showRequiredError = true;
        }
        break;

      case 'Non-Strict-Required':
        field.nonStrictRequired = true;
        break;

      case 'Assign':
        field.disable = !field.options.overrideCalculation;
        if (args.length === 2) {
          // NB. First element is an identifier but we're currrently ignoring it
          [, condition] = args;
        } else if (args.length === 1) {
          [condition] = args;
        }
        // eslint-disable-next-line no-case-declarations
        const expressionValue = evaluateCondition(condition, formFields);
        if (expressionValue !== undefined && expressionValue !== null) {
          if (field.type === FormFieldType.DATE && !isNaN(expressionValue)) {
            if (expressionValue > 0 && !(field.options.overrideCalculation && field.overrideCalculatedValue)) {
              setFieldvalue(field, expressionValue);
            }
          } else if (field.type === FormFieldType.TEXT || field.type === FormFieldType.CHOICE) {
            //For text field no need to check if the value is NaN.
            setFieldvalue(field, expressionValue);
          } else if (!isNaN(expressionValue)) {
            setFieldvalue(field, expressionValue);
          }
        } else if (field.type === FormFieldType.DATE) {
          field.dateValue = undefined;
        }
        break;
    }
  });

  //unit assignment
  const unitFields = formFields.filter(f => f.type === FormFieldType.UNIT && f.options.units && f.options.units.list.length === 1);

  for (const unitField of unitFields) {
    if (unitField.options.units?.list[0]?.fromFieldValue?.includes(field.identifier) && getFieldValue(field)) {
      unitField.options.units!.list[0].unit = `${getFieldValue(field)}`;
      unitField.value = unitField.options.units!.list[0].unit;
    }
  }
};

export const resetCalculatedValue = (resettingField: FormField, formFields: FormField[]) => {
  for (const field of formFields) {
    if (field.identifier != resettingField.identifier) {
      if (JSON.stringify(field.conditions).includes(resettingField.identifier)) {
        clearFieldValue(field);
        resetCalculatedValue(field, formFields);
      }
    }
  }
};

const ACTIONS_ORDER: EffectType[] = ['Assign', 'Show', 'Warn', 'Error', 'Valid', 'Enable', 'Disable', 'Hide'];

const sortByActions = (data: Effect[], actionsOrder: EffectType[]): Effect[] => {
  return data.sort((a, b) => {
    const actionA = a[0]; // Extract the action from the first element of sub-array
    const actionB = b[0];
    const indexA = actionsOrder.indexOf(actionA);
    const indexB = actionsOrder.indexOf(actionB);

    // If either action is not in the provided order array, treat it as lower priority
    return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
  });
};

export const getFieldValue = (field: FormField) => {

  switch (field.type) {
    case FormFieldType.TEXT:
      return field.value;

    case FormFieldType.SLIDER:
    case FormFieldType.NUMBER:
      return field.numberValue;

    case FormFieldType.UNIT:
      // eslint-disable-next-line no-case-declarations
      const parentUnit = field.options.units?.list.find(unit => unit.id === field.options.units?.default);

      // eslint-disable-next-line no-case-declarations
      let numberValue = field.numberValue;
      if (field.numberValue && parentUnit && parentUnit?.unit !== field.value && parentUnit.conversions) {
        for (const conversion of parentUnit.conversions as Effect[]) {
          const [unit, conversionFormula] = conversion;
          if (unit === field.value) {
            const [operator, number] = conversionFormula;

            if (operator === '*') {
              numberValue = field.numberValue * number;
            } else if (operator === '/') {
              numberValue = field.numberValue / number;
            }
            break;
          }
        }
      }

      return numberValue;

    case FormFieldType.DROPDOWN:
    case FormFieldType.CHOICE:
      // eslint-disable-next-line no-case-declarations
      let value: string | number = '';
      if (field.options.maximumChoicesCount == 1) {
        value = (field.choiceValue as FormFieldChoice)?.value;
        if (!value && field.options.allowOther && field.otherChoiceValue?.value === OTHER_CHOICE_CODE) {
          value = field.otherChoiceValue?.otherValue ?? '';
        }
      } else {
        const selectedChoices: string[] = [];
        if (field.type === FormFieldType.CHOICE) {

          field.options.choices?.forEach(choice => {
            if (choice.checked) {
              selectedChoices.push(`${choice.value}`);
            }
          });

          if (field.options.allowOther && field.otherChoiceValue?.checked) {
            selectedChoices.push(field.otherChoiceValue?.otherValue ?? '');
          }
        } else {
          if (Array.isArray(field.choiceValue)) {
            (field.choiceValue as FormFieldChoice[])?.forEach(choice => {
              selectedChoices.push(`${choice.value}`);
            });
          }
        }

        value = selectedChoices.join(',');
      }
      return value;

    case FormFieldType.DATE:
      if (field.options.mode === DateMode.TIME) {
        return field.dateValue ? getOnlyTimeSeconds(field.dateValue) : undefined;
      } else if (field.options.mode === DateMode.DATE_TIME) {
        if (field.options.absoluteDate) {
          return field.dateValue ? Math.floor(getUTCSeconds(field.dateValue)) : undefined;
        } else {
          return field.dateValue ? DateTime.fromJSDate(field.dateValue).toSeconds() : undefined;
        }
      }
      return field.dateValue ? getUTCSecondsAtStartOfDay(field.dateValue) : undefined;

    case FormFieldType.LOCATION:
      return field.locationValue;

    case FormFieldType.FILE:
      if (field.fileIds) {
        return value = JSON.stringify(field.fileIds);
      }
      break;

    case FormFieldType.SIGNATURE:
      return field.signatureValue;

    case FormFieldType.MATRIX:
      return field.matrixTableRowValues;

    case FormFieldType.DRILLDOWN:
      return field.drillValue;

    default:
      break;
  }
};

const clearFieldValue = (field: FormField) => {

  switch (field.type) {
    case FormFieldType.TEXT:
      field.value = '';
      break;

    case FormFieldType.SLIDER:
    case FormFieldType.NUMBER:
    case FormFieldType.UNIT:
      field.numberValue = undefined;
      break;

    case FormFieldType.DROPDOWN:
      if (field.options.maximumChoicesCount == 1) {
        // @ts-expect-error just initialising the null value
        field.choiceValue = null;
      } else {
        field.choiceValue = [];
      }
      break;
    case FormFieldType.CHOICE:
      field.choiceValue = FormFieldChoice.createEmpty();
      field.otherChoiceValue = {
        id: 99999999,
        label: 'Other',
        checked: false,
        otherValue: '',
        value: '-1'
      };
      break;

    case FormFieldType.DATE:
      field.dateValue = undefined;
      field.timezoneRecorded = undefined;
      break;

    case FormFieldType.LOCATION:
      field.locationValue = undefined;
      break;

    case FormFieldType.FILE:
      field.fileIds = undefined;
      break;

    case FormFieldType.SIGNATURE:
      field.signatureValue = undefined;
      break;

    case FormFieldType.MATRIX:
      // TODO: clear values inside the matrixTableRowValues
      field.matrixTableRowValues = undefined;
      break;

    case FormFieldType.DRILLDOWN:
      field.drillValue = undefined;
      break;

    default:
      field.value = '';
      break;
  }
};

const setFieldvalue = (field: FormField, value: string | number) => {
  switch (field.type) {
    case FormFieldType.TEXT:
      field.value = value as string;
      break;

    case FormFieldType.CHOICE:
      if (field.options.maximumChoicesCount == 1) {
        if (value) {
          field.choiceValue = field.options.choices?.find(choice => choice.value == value);
        }
        if (!field.choiceValue) {
          field.choiceValue = FormFieldChoice.createEmpty();
        }
      } else {
        const valueChoices = (value as string)?.split(',');
        if (valueChoices) {
          valueChoices.forEach(choiceValue => {
            for (const choice of field.options.choices!) {
              if (choice.value == choiceValue) {
                choice.checked = true;
                break;
              }
            }
          });
        }

        for (const choice of field.options.choices!) {
          if (choice.checked === undefined) {
            choice.checked = false;
          }
        }

        if (field.options.allowOther) {
          let otherValue = '';
          if (valueChoices) {
            for (const valueChoice of valueChoices) {
              let hasOtherValue = true;
              for (const choice of field.options.choices!) {
                if (`${choice.value}` === valueChoice) {
                  hasOtherValue = false;
                  break;
                }
              }
              if (hasOtherValue) {
                otherValue = valueChoice;
                break;
              }
            }
          }

          field.otherChoiceValue = {
            id: 99999999,
            label: 'Other',
            checked: otherValue !== '',
            otherValue: otherValue,
            value: otherValue ? OTHER_CHOICE_CODE : '-1'
          };
        }
      }
      break;

    case FormFieldType.SLIDER:
    case FormFieldType.NUMBER:
    case FormFieldType.UNIT:
      field.numberValue = value as number;
      field.inputChanged = true;
      break;

    case FormFieldType.DATE:
      if (field.options.mode === DateMode.DATE_TIME) {
        if (field.options.absoluteDate) {
          field.dateValue = getUTCJSDate(parseInt(value as string, 10));
        } else {
          field.dateValue = DateTime.fromSeconds(parseInt(value as string)).toJSDate();
        }
      } else if (field.options.mode === DateMode.DATE) {
        field.dateValue = getUTCJSDate(parseInt(value as string, 10));
      } else if (field.options.mode === DateMode.TIME) {
        field.dateValue = getTimeFromSeconds(parseInt(value as string, 10));
      }
      break;

    default:
      break;
  }
};

export const evaluateCondition = (
  condition: Condition,
  formFields: FormField[]
):
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any => {
  if (!Array.isArray(condition)) {
    let value = condition;
    if (typeof value === 'string' && value.includes('|')) {
      // this is very specific formula for PUMP, 
      // POST_OP_LAB_AST|POST_OP_LAB_DAY==0
      value = matrixFieldValue1(value, formFields);
    } else if (typeof value === 'string' && value.startsWith('@')) {
      const identifier = condition.substring(1).toLowerCase();
      const valueField = formFields.find(field => field.identifier === identifier);
      if (valueField) {
        value = getFieldValue(valueField);
      }
    } else if (value === '#now') {
      value = DateTime.now().toSeconds();
    }
    return value;
  }

  const [operator, ...args] = condition;
  const evaluatedArgs = args.map(arg => evaluateCondition(arg, formFields));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let parsedArgs: any[], reducer: (accum: any, curr: any) => any;
  switch (operator) {
    // Logical operators
    case '&&': // every
      parsedArgs = evaluatedArgs.map(parseValue);
      return parsedArgs.every(el => el);
    case '!&&': // not every
      parsedArgs = evaluatedArgs.map(parseValue);
      return !parsedArgs.every(el => el);
    case '||': // any
      parsedArgs = evaluatedArgs.map(parseValue);
      return parsedArgs.some(el => el);
    case '!||': // not any
      parsedArgs = evaluatedArgs.map(parseValue);
      return !parsedArgs.some(el => el);
    case '!': // not
      parsedArgs = evaluatedArgs.map(parseValue);
      return !parsedArgs[0];
    // Comparison operators
    case '==': // equal
      parsedArgs = evaluatedArgs.map(parseValue);
      return parsedArgs.every(el => el == parsedArgs[0]);
    case '!=': // not equal
      parsedArgs = evaluatedArgs.map(parseValue);
      return !parsedArgs.every(el => el == parsedArgs[0]);
    case '<': // strictly ascending
      parsedArgs = evaluatedArgs.map(parseValue);
      return parsedArgs.every((elt, idx, arr) => {
        const prevIdx = Math.max(idx - 1, 0);
        const prev = arr[prevIdx];
        return prevIdx === idx || prev < elt;
      });
    case '<=': // ascending
      parsedArgs = evaluatedArgs.map(parseValue);
      return parsedArgs.every((elt, idx, arr) => {
        const prevIdx = Math.max(idx - 1, 0);
        const prev = arr[prevIdx];
        return prevIdx === idx || prev <= elt;
      });
    case '>': // strictly descending
      parsedArgs = evaluatedArgs.map(parseValue);
      return parsedArgs.every((elt, idx, arr) => {
        const prevIdx = Math.max(idx - 1, 0);
        const prev = arr[prevIdx];
        return prevIdx === idx || prev > elt;
      });
    case '>=': // descending
      parsedArgs = evaluatedArgs.map(parseValue);
      return parsedArgs.every((elt, idx, arr) => {
        const prevIdx = Math.max(idx - 1, 0);
        const prev = arr[prevIdx];
        return prevIdx === idx || prev >= elt;
      });
    case '=': // assign
      return evaluatedArgs[0];
    // Arithmetic operators
    case '+':
      parsedArgs = evaluatedArgs.map(parseValue);
      reducer = (accum, curr) => Number(accum) + Number(curr);
      return parsedArgs.reduce(reducer);
    case '-':
      parsedArgs = evaluatedArgs.map(parseValue);
      reducer = (accum, curr) => Number(accum) + Number(curr);
      return -1 * parsedArgs.reduce(reducer);
    case '*':
      parsedArgs = evaluatedArgs.map(parseValue);
      reducer = (accum, curr) => Number(accum) * Number(curr);
      return parsedArgs.reduce(reducer);
    case '/':
      parsedArgs = evaluatedArgs.map(parseValue);
      reducer = (accum, curr) => Number(accum) + Number(curr);
      return 1 / parsedArgs.reduce(reducer);
    case '%':
      parsedArgs = evaluatedArgs.map(parseValue);
      return parsedArgs[0] % parsedArgs[1];
    // Conditional operators
    case '?': // existent
      parsedArgs = evaluatedArgs.map(parseValue);
      return parsedArgs.every(el => el !== null && el !== undefined);
    case '??': // coalesced
      parsedArgs = evaluatedArgs.map(parseValue);
      return parsedArgs[0] !== null ? parsedArgs[0] : parsedArgs[1];
    case '?::': // switch statement
      parsedArgs = evaluatedArgs.map(parseValue);
      for (let i = 0; i < parsedArgs.length - 1; i += 2) {
        if (parsedArgs[i]) {
          return parsedArgs[i + 1];
        }
      }
      return parsedArgs[parsedArgs.length - 1];
    case '?:': // nested-if-then-else
      parsedArgs = evaluatedArgs.map(parseValue);
      for (let i = 0; i < (parsedArgs.length - 1) / 2; i += 1) {
        if (parsedArgs[i]) {
          return parsedArgs[i + (parsedArgs.length - 1) / 2];
        }
      }
      return parsedArgs[parsedArgs.length - 1];
    // Set operators
    case '⊂': // strict subset
      return evaluatedArgs.every((elt, idx, arr) => {
        const prevIdx = Math.max(idx - 1, 0);
        const prev = arr[prevIdx];
        return prevIdx === idx || evaluateSubset(prev, elt, true);
      });
    case '⊂=': // subset
      return evaluatedArgs.every((elt, idx, arr) => {
        const prevIdx = Math.max(idx - 1, 0);
        const prev = arr[prevIdx];
        const evaluation = prevIdx === idx || evaluateSubset(prev, elt, false);
        return evaluation;
      });
    case '⊃': // strict superset
      return evaluatedArgs.every((elt, idx, arr) => {
        const prevIdx = Math.max(idx - 1, 0);
        const prev = arr[prevIdx];
        return prevIdx === idx || evaluateSubset(elt, prev, true);
      });
    case '⊃=': // superset
      return evaluatedArgs.every((elt, idx, arr) => {
        const prevIdx = Math.max(idx - 1, 0);
        const prev = arr[prevIdx];
        return prevIdx === idx || evaluateSubset(elt, prev, false);
      });
    case 'max':
      return Math.max(...evaluatedArgs.map(value => typeof value === 'number' && value !== undefined ? value : 0));
    case 'min':
      if (evaluatedArgs.filter(value => value !== undefined).map(value => value).length > 0) {
        return Math.min(...evaluatedArgs.filter(value => value !== undefined).map(value => value));
      }
      return undefined;
    case 'log':
      // this should have only one param
      return Math.log(evaluatedArgs[0]);

    case 'regex':
      // eslint-disable-next-line no-case-declarations
      const regex = new RegExp(evaluatedArgs[0]);
      // The first argument must always be a regular expression, and the second should be the referenced expression,
      // nothing but identifier of the field, evaluated to its value.
      return !regex.test(evaluatedArgs[1]);

    // this is very specific operator implemented for PUMP
    case 'hours':
      return evaluatedArgs[0] ? convertStringHoursToNumberHours(evaluatedArgs[0]) : undefined;

    case 'roundDown':
      if (evaluatedArgs[0] && evaluatedArgs[1] !== undefined && evaluatedArgs[1] !== null) {
        return roundNumber(evaluatedArgs[0], evaluatedArgs[1], 'down');
      }
      return undefined;

    //TODO: Implement 'roundUp' and 'round' operators
  }
};

export const evaluateSubset = (
  smallSet: string | null | undefined,
  bigSet: string | null | undefined,
  strict: boolean
): boolean => {
  const smallElems = smallSet ? `${smallSet}`.split(',') : [];
  const bigElems = bigSet ? `${bigSet}`.split(',') : [];

  for (const smallElem of smallElems) {
    if (smallElem !== '' && !bigElems.includes(smallElem)) {
      return false;
    }
  }

  if (strict && smallSet?.length !== bigSet?.length) {
    return false;
  }

  return true;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseValue = (value: any) => {
  let parsed = value;
  if (value === '') {
    return null;
  } else if (value === null) {
    parsed = '';
  } else if (typeof value === 'number' && !isNaN(value)) {
    parsed = value;
  }
  return parsed;
};

export const FIELD_REGEX = /@([a-z0-9]+)/;

export const resolveReferences = (
  fields: FormField[],
  unresolved?: string
): string => {
  const pieces = (unresolved || '').split(' ');

  return pieces
    .map(piece => {
      const match = piece.match(FIELD_REGEX);
      if (match) {
        const [, identifier] = match;
        const field = fields.find(f => f.identifier === identifier);

        if (field) {
          let resolved: string;
          const selected = getFieldValue(field);

          switch (field.type) {
            case 'Choice': {
              const getLabel = (key: string) =>
                field.options?.choices?.find(choice => `${choice.value}` === `${key}`)?.label || '...';

              if (field.options?.maximumChoicesCount === 1) {
                resolved = getLabel(selected as string) || '...';
              } else {
                resolved = Object.entries(selected || {})
                  .filter(([, val]) => val)
                  .map(([key]) => getLabel(key))
                  .join(', ') || '...';
              }
              break;
            }
            case 'Dropdown': {
              if (field.options?.maximumChoicesCount === 1) {
                resolved = selected ? selected as string : '...';
              } else {
                // @ts-expect-error just using map
                resolved = (selected || []).map((v: FormField) => v.label).join(', ') || '...';
              }
              break;
            }
            default: {
              resolved = `${selected || '...'}${piece.endsWith(',') ? ',' : ''}${piece.endsWith('.') ? '.' : ''}`;
              break;
            }
          }
          return resolveReferences(fields, resolved);
        }
      }
      return piece;
    })
    .join(' ');
};

export const isAssignEffectPresent = (field: FormField): boolean => {
  const effects = sortByActions(field.conditions as Effect[], ACTIONS_ORDER);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return (effects as Effect[]).find(([type, ...args]: Condition[]) => type === 'Assign') !== undefined;
};

const matrixFieldValue1 = (value: string, formFields: FormField[]) => {
  let finalValue: unknown = undefined;
  const valueParts = value.split('|');
  const variableName = valueParts[0];
  const valueField = formFields.find(field => field.variableName === variableName);
  if (valueField) {
    finalValue = getFieldValue(valueField);
  } else {
    //no regular field found, so check the matrix fields
    const matrixFields = formFields.filter(field => field.type === FormFieldType.MATRIX);
    let matrixField;
    let firstColumnID: string;
    if (matrixFields.length > 0) {
      for (const field of matrixFields) {
        for (const column of field.options.columns!) {
          if (variableName === column.variableName) {
            matrixField = field;
            firstColumnID = `${column.id}`;
            break;
          }
        }
      }
    }

    if (matrixField && matrixField.matrixTableRowValues) {
      let conditionsParts: string[] | undefined = !valueParts[1].includes('<') ? valueParts[1].split('==') : undefined;
      if (conditionsParts && conditionsParts.length === 2) {
        // POST_OP_LAB_AST|POST_OP_LAB_DAY==0
        const match = conditionsParts[0].match(FIELD_VARIABLE_REGEX);
        if (match) {
          const secondColumnID = matrixField.options.columns!.find(column => column.variableName === match[0])?.id;

          // eslint-disable-next-line no-restricted-syntax
          loop: for (const matrixRow of matrixField.matrixTableRowValues!) {
            for (const columnValue of matrixRow.columnValues!) {
              if (columnValue.id === secondColumnID && columnValue.type === MatrixColumnType.DROPDOWN) {
                if (`${(columnValue.choiceValue as FormFieldChoice)?.id}` === conditionsParts[1]) {
                  finalValue = matrixRow.columnValues?.find(colValue => `${colValue.id}` == `${firstColumnID}`)?.numberValue;
                  break loop;
                }
              }
            }
          }
        }

        conditionsParts = undefined;
      }

      conditionsParts = valueParts[1].includes('<') ? valueParts[1].split('<') : undefined;

      if (conditionsParts && conditionsParts.length === 2) {
        //POST_OP_LAB_DATE|POST_OP_LAB_AST<2000|POST_OP_LAB_DAY==0
        const match = conditionsParts[0].match(FIELD_VARIABLE_REGEX);
        if (match) {
          // eslint-disable-next-line no-restricted-syntax
          loop: for (const matrixRow of matrixField.matrixTableRowValues!) {
            for (const columnValue of matrixRow.columnValues!) {
              if (valueParts[2].includes('==')) {
                const thirdConditions = valueParts[2].split('==');
                const match2 = thirdConditions[0].match(FIELD_VARIABLE_REGEX);
                if (match2) {
                  if (columnValue.variableName === thirdConditions[0] &&
                    `${(columnValue.choiceValue as FormFieldChoice)?.id}` == `${thirdConditions[1]}`) {
                    const secondConditions = valueParts[1].split('<');
                    for (const colVal of matrixRow.columnValues!) {
                      if (colVal.variableName === secondConditions[0] &&
                        colVal.numberValue && colVal.numberValue < parseInt(secondConditions[1], 0)) {
                        const dateValue = matrixRow.columnValues?.find(colValue => `${colValue.id}` == `${firstColumnID}`)?.dateValue;
                        if (dateValue) {
                          if (matrixRow.columnValues?.find(colValue => `${colValue.id}` == `${firstColumnID}`)?.options?.mode === DateMode.TIME) {
                            finalValue = getOnlyTimeSeconds(dateValue);
                          } else {
                            finalValue = Math.floor(getUTCSeconds(dateValue));
                          }
                        } else {
                          finalValue = undefined;
                        }
                        break loop;
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  return finalValue;
};
